import React, { useState, useMemo } from "react";
import { Info, Loader, ChevronDown, ChevronRight } from "lucide-react";
import SearchableSelect from "../SearchableSelect";

export default function StockTable({
  data,
  loading,
  error,
  onInfo,
  onPageChange,
  currentPage,
  itemsPerPage,
  totalEntries,
  startEntry,
  endEntry,
  maxPages,
  onItemsPerPageChange,
  conversionRules = [],
}) {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (productId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const aggregateStock = (variants, key, includeUnit = true) => {
    const totals = {};
    variants.forEach((v) => {
      const val = Number(v[key]) || 0;
      const unit = (v.unit_name || "PCS").toUpperCase();
      totals[unit] = (totals[unit] || 0) + val;
    });
    return Object.entries(totals)
      .map(([unit, val]) => (includeUnit ? `${val} ${unit}` : `${val}`))
      .join(", ");
  };

  const normalizeValue = (value) => String(value || "").trim().toLowerCase();

  const findConversionRule = (stock) => {
    const productId = normalizeValue(stock.product_id);
    const variantId = normalizeValue(stock.variant_id);
    const stockUnit = normalizeValue(stock.unit_name);

    const candidates = conversionRules.filter((rule) => {
      if (rule.is_active === false || normalizeValue(rule.product_id) !== productId) {
        return false;
      }

      const ruleUnit = normalizeValue(rule.to_unit_name);
      return !stockUnit || !ruleUnit || ruleUnit === stockUnit;
    });

    return (
      candidates.find((rule) => normalizeValue(rule.variant_id) === variantId && variantId) ||
      candidates.find((rule) => !rule.variant_id) ||
      candidates.find((rule) => normalizeValue(rule.variant_id) === variantId)
    );
  };

  const formatQuantityInt = (value) =>
    new Intl.NumberFormat("id-ID", {
      maximumFractionDigits: 0,
    }).format(Math.floor(value));

  const getConversionText = (stock, key = "current_stock") => {
    const rule = findConversionRule(stock);
    const factor = Number(rule?.conversion_factor);
    const value = Number(stock[key]);

    if (!rule || !Number.isFinite(factor) || factor <= 0 || !Number.isFinite(value)) {
      return null;
    }

    return `≈ ${formatQuantityInt(value / factor)} ${(rule.from_unit_name || "unit").toUpperCase()}`;
  };

  const getAggregatedConversionText = (variants, key = "current_stock") => {
    const conversions = variants.map((variant) => {
      const rule = findConversionRule(variant);
      const factor = Number(rule?.conversion_factor);
      const value = Number(variant[key]);

      if (!rule || !Number.isFinite(factor) || factor <= 0 || !Number.isFinite(value)) {
        return null;
      }

      return {
        rule,
        factor,
        value,
        unit: (rule.from_unit_name || "unit").toUpperCase(),
      };
    });

    if (
      conversions.some((conversion) => !conversion) ||
      new Set(
        conversions.map(
          ({ rule, factor }) => `${normalizeValue(rule.from_unit_name)}-${factor}-${normalizeValue(rule.to_unit_name)}`
        )
      ).size > 1
    ) {
      return "";
    }

    const conversion = conversions[0];
    if (!conversion) return "";

    const total = conversions.reduce(
      (sum, item) => sum + item.value / item.factor,
      0
    );
    return `≈ ${formatQuantityInt(total)} ${conversion.unit}`;
  };

  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";

  // Group items by product_id
  const groupedProducts = useMemo(() => {
    const map = {};
    const filteredData = isApps
      ? data.filter((item) => !item.brand_name && !item.brand_id)
      : data;

    filteredData.forEach((item) => {
      const key = item.product_id || item.id;
      if (!map[key]) {
        map[key] = {
          id: key,
          product_name: item.product_name || item.name,
          product_code: item.product_code || "-",
          item_type: item.item_type || "PRODUCT",
          variants: [],
        };
      }
      map[key].variants.push(item);
    });

    // Filter out DEFAULT variant if there are other variants or options
    const result = Object.values(map);
    result.forEach(product => {
      if (product.variants.length > 1) {
        product.variants = product.variants.filter(v => v.variant_id != null || v.option_id != null);
      }
    });

    return result;
  }, [data, isApps]);
  return (
    <div className="bg-white rounded-lg border border-gray-200 mt-6">
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {error && !loading && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-700">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-16">
                    Aksi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Nama Produk
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Stock Awal
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Stock Masuk
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Stock Keluar
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Stock Akhir
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    Minimum Stock
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {groupedProducts.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-8 text-center text-gray-500">
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  groupedProducts.map((product) => {
                    const hasMultiple = product.variants.length > 1;
                    const isExpanded = !!expandedRows[product.id];
                    const aggregatedConversionText = getAggregatedConversionText(product.variants);

                    if (hasMultiple) {
                      return (
                        <React.Fragment key={product.id}>
                          {/* Parent Row */}
                          {(() => {
                            const hasLowStockVariant = product.variants.some(
                              (v) => v.current_stock <= v.minimum_stock
                            );
                            const parentBgClass = hasLowStockVariant
                              ? "bg-orange-50/70 hover:bg-orange-100/70 transition-colors font-semibold"
                              : "hover:bg-slate-50 transition-colors bg-white font-semibold";
                            const chevronButtonClass = hasLowStockVariant
                              ? "w-8 h-8 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg flex items-center justify-center transition-colors shadow-sm"
                              : "w-8 h-8 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg flex items-center justify-center transition-colors";

                            return (
                              <tr className={parentBgClass}>
                                <td className="px-6 py-4">
                                  <button
                                    onClick={() => toggleRow(product.id)}
                                    className={chevronButtonClass}
                                    title={isExpanded ? "Collapse" : "Expand"}>
                                    {isExpanded ? (
                                      <ChevronDown className="w-4 h-4" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4" />
                                    )}
                                  </button>
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-900">
                                  <div className="font-bold text-slate-800">{product.product_name}</div>
                                  <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                    {product.product_code && product.product_code !== "-" && (
                                      <span className="inline-flex items-center text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200 font-semibold px-1.5 py-0.5 rounded">
                                        Kode: {product.product_code}
                                      </span>
                                    )}
                                    <span className="text-xs text-teal-600 font-medium">
                                      {product.variants.length} Varian
                                    </span>
                                    {hasLowStockVariant && (
                                      <span className="text-[10px] bg-red-100 text-red-600 border border-red-200 font-bold px-1.5 py-0.5 rounded animate-pulse">
                                        Stok Menipis!
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-center text-slate-600 text-xs font-normal">
                                  {aggregateStock(product.variants, "beginning_stock", false)}
                                </td>
                                <td className="px-6 py-4 text-center text-slate-600 text-xs font-normal">
                                  {aggregateStock(product.variants, "stock_in", false)}
                                </td>
                                <td className="px-6 py-4 text-center text-slate-600 text-xs font-normal">
                                  {aggregateStock(product.variants, "stock_out", false)}
                                </td>
                                <td className="px-6 py-4 text-center text-xs font-semibold">
                                  <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                    <span className={hasLowStockVariant ? "text-red-600" : "text-slate-800"}>
                                      {aggregateStock(product.variants, "current_stock", true)}
                                    </span>
                                    {aggregatedConversionText && (
                                      <span className="text-[11px] font-medium text-teal-600 whitespace-nowrap">
                                        {aggregatedConversionText}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-center text-slate-500 text-xs font-normal">
                                  <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                    <span>{aggregateStock(product.variants, "minimum_stock", true)}</span>
                                    {(() => {
                                      const minConversionText = getAggregatedConversionText(product.variants, "minimum_stock");
                                      return minConversionText ? (
                                        <span className="text-[11px] font-medium text-teal-600 whitespace-nowrap">
                                          {minConversionText}
                                        </span>
                                      ) : null;
                                    })()}
                                  </div>
                                </td>
                              </tr>
                            );
                          })()}

                          {/* Expanded Variant Rows */}
                          {isExpanded &&
                            product.variants.map((v) => {
                              const isLowStock = v.current_stock <= v.minimum_stock;
                              const conversionText = getConversionText(v);
                              return (
                                <tr
                                  key={v.id}
                                  className={`transition border-l-4 border-teal-500/30 ${
                                    isLowStock ? "bg-orange-50/50 hover:bg-orange-100/50" : "bg-slate-50/40 hover:bg-slate-100/50"
                                  }`}>
                                  <td className="px-6 py-3 pl-8">
                                    <button
                                      onClick={() => onInfo(v.id)}
                                      className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors shadow-sm"
                                      title="Detail">
                                      <Info className="w-3.5 h-3.5" />
                                    </button>
                                  </td>
                                  <td className="px-6 py-3 text-sm text-gray-800">
                                    <div className="pl-4 text-xs text-gray-600 font-medium">
                                      {v.option_id ? (
                                        `↳ Opsi ${v.option_name}`
                                      ) : v.brand_name ? (
                                        `↳ Merek ${v.brand_name}`
                                      ) : (
                                        `↳ Varian ${v.variant_name || "DEFAULT"}`
                                      )} • Satuan {v.unit_name || "PCS"}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-sm text-gray-600 text-center">
                                    {v.beginning_stock || 0}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-gray-600 text-center">
                                    {v.stock_in || 0}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-gray-600 text-center">
                                    {v.stock_out || 0}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-center">
                                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                      <span
                                        className={`font-semibold ${isLowStock ? "text-red-600" : "text-gray-800"}`}>
                                        {v.current_stock} {v.unit_name || "PCS"}
                                      </span>
                                      {conversionText && (
                                        <span className="text-[11px] font-medium text-teal-600 whitespace-nowrap">
                                          {conversionText}
                                        </span>
                                      )}
                                      {isLowStock && (
                                        <span className="text-xs text-red-500 font-medium">
                                          (Low)
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                      {isLowStock ? (
                                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-500 text-white">
                                          {v.minimum_stock} {v.unit_name || "PCS"}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-600 font-medium">
                                          {v.minimum_stock} {v.unit_name || "PCS"}
                                        </span>
                                      )}
                                      {(() => {
                                        const minConversionText = getConversionText(v, "minimum_stock");
                                        return minConversionText ? (
                                          <span className="text-[11px] font-medium text-teal-600 whitespace-nowrap">
                                            {minConversionText}
                                          </span>
                                        ) : null;
                                      })()}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    } else {
                      // Single variant - render as a flat row
                      const v = product.variants[0];
                      const isLowStock = v.current_stock <= v.minimum_stock;
                      const conversionText = getConversionText(v);
                      return (
                        <tr
                          key={v.id}
                          className={`transition ${isLowStock ? "bg-orange-50 hover:bg-orange-100" : "hover:bg-gray-50"}`}>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => onInfo(v.id)}
                              className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                              title="Detail">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="font-semibold text-slate-800">{product.product_name}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-gray-500">
                              {product.product_code && product.product_code !== "-" && (
                                <span className="inline-flex items-center text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200 font-semibold px-1.5 py-0.5 rounded">
                                  Kode: {product.product_code}
                                </span>
                              )}
                              <span className="font-normal">
                                {v.option_id ? (
                                  `Opsi ${v.option_name} • `
                                ) : v.brand_name ? (
                                  `Merek ${v.brand_name} • `
                                ) : v.variant_name ? (
                                  `Varian ${v.variant_name} • `
                                ) : ""}Satuan {v.unit_name || "PCS"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 text-center">
                            {v.beginning_stock || 0}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 text-center">
                            {v.stock_in || 0}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 text-center">
                            {v.stock_out || 0}
                          </td>
                          <td className="px-6 py-4 text-sm text-center">
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              <span
                                className={`font-semibold ${isLowStock ? "text-red-600" : "text-gray-900"}`}>
                                {v.current_stock} {v.unit_name || "PCS"}
                              </span>
                              {conversionText && (
                                <span className="text-[11px] font-medium text-teal-600 whitespace-nowrap">
                                  {conversionText}
                                </span>
                              )}
                              {isLowStock && (
                                <span className="text-xs text-red-500 font-medium">
                                  (Low)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              {isLowStock ? (
                                <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium bg-orange-500 text-white">
                                  {v.minimum_stock} {v.unit_name || "PCS"}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-600">
                                  {v.minimum_stock} {v.unit_name || "PCS"}
                                </span>
                              )}
                              {(() => {
                                const minConversionText = getConversionText(v, "minimum_stock");
                                return minConversionText ? (
                                  <span className="text-[11px] font-medium text-teal-600 whitespace-nowrap">
                                    {minConversionText}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {startEntry} to {endEntry} of {totalEntries} entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => onItemsPerPageChange(value)}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = maxPages - 2;
                else p = currentPage - 1;

                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ›
              </button>
              <button
                onClick={() => onPageChange(maxPages)}
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
